import type { FieldError } from 'react-hook-form'

/**
 * Generates user-friendly error messages in German based on error type and field context
 */
export const getFieldErrorMessage = (error: FieldError | undefined, fieldName?: string): string => {
  if (!error) return ''

  const { type, message } = error

  // Field-specific context for better messages
  const fieldContext: Record<string, string> = {
    heatedVolumeInCbm: 'Beheiztes Volumen',
    usableSpaceInSqm: 'Nutzfläche',
    constructionYear: 'Baujahr',
    numberOfResidentialUnits: 'Anzahl der Wohneinheiten',
    numberOfFloors: 'Anzahl der Stockwerke',
    meanFloorHeightInCm: 'Mittlere Geschosshöhe',
    finalEnergyDemandInKwh: 'Endenergiebedarf',
    auxEnergyDemandInKwh: 'Hilfsenergiebedarf',
    energyConsumptionInKwh: 'Energieverbrauch',
    primaryEnergyDemandInKwh: 'Primärenergiebedarf',
    transmissionHeatLossInWPerK: 'Transmissionswärmeverlust',
    equivalentCo2EmissionsInTons: 'Äquivalente CO₂ Emissionen',
    areaInSqm: 'Fläche',
    uValue: 'U-Wert',
    orientation: 'Ausrichtung',
    borderingType: 'Angrenzender Bereich',
    generatorType: 'Generatortyp',
    energySource: 'Energiequelle',
    type: 'Typ',
    coverageInPct: 'Abdeckung',
    ventilationType: 'Lüftungstyp',
    heatRecoveryRateInPct: 'Wärmerückgewinnungsrate',
  }

  // Extract field name from the path if available
  const contextName = fieldName?.split('.').pop() || ''
  const fieldLabel = fieldContext[contextName] || contextName

  // Generate messages based on error type
  switch (type) {
    case 'required':
      return fieldLabel ? `${fieldLabel} ist erforderlich` : 'Dieses Feld ist erforderlich'

    case 'min':
      // Check if it's a number validation
      if (message?.includes('0')) {
        return fieldLabel
          ? `${fieldLabel} muss größer oder gleich 0 sein`
          : 'Der Wert muss größer oder gleich 0 sein'
      }
      return fieldLabel ? `${fieldLabel} ist zu klein` : 'Der Wert ist zu klein'

    case 'max':
      // For percentage fields
      if (contextName.includes('Pct') || contextName.includes('percentage')) {
        return fieldLabel
          ? `${fieldLabel} darf maximal 100% betragen`
          : 'Der Wert darf maximal 100% betragen'
      }
      return fieldLabel ? `${fieldLabel} ist zu groß` : 'Der Wert ist zu groß'

    case 'invalid_type':
      // Check what type is expected
      if (message?.includes('number')) {
        return fieldLabel
          ? `${fieldLabel} muss eine gültige Zahl eingegeben werden`
          : 'Es muss eine gültige Zahl eingegeben werden'
      }
      if (message?.includes('integer')) {
        return fieldLabel
          ? `${fieldLabel} muss eine ganze Zahl eingegeben werden`
          : 'Es muss eine ganze Zahl eingegeben werden'
      }
      if (message?.includes('string')) {
        return fieldLabel
          ? `${fieldLabel} muss Text eingegeben werden`
          : 'Es muss Text eingegeben werden'
      }
      return 'Bitte geben Sie einen gültigen Wert ein'

    case 'invalid_enum_value':
      return 'Bitte wählen Sie eine gültige Option aus'

    case 'too_small':
      // Zod's min validation
      if (message?.includes('Number must be greater than or equal to 0')) {
        return fieldLabel
          ? `${fieldLabel} muss mindestens 0 sein`
          : 'Der Wert muss mindestens 0 sein'
      }
      if (message?.includes('String must contain at least')) {
        const minLength = message.match(/\d+/)?.[0]
        return fieldLabel
          ? `${fieldLabel} muss mindestens ${minLength} Zeichen lang sein`
          : `Der Text muss mindestens ${minLength} Zeichen lang sein`
      }
      return fieldLabel ? `${fieldLabel} ist zu klein` : 'Der Wert ist zu klein'

    case 'too_big':
      // Zod's max validation
      if (message?.includes('Number must be less than or equal to 100')) {
        return fieldLabel ? `${fieldLabel} darf maximal 100 sein` : 'Der Wert darf maximal 100 sein'
      }
      if (message?.includes('String must contain at most')) {
        const maxLength = message.match(/\d+/)?.[0]
        return fieldLabel
          ? `${fieldLabel} darf maximal ${maxLength} Zeichen lang sein`
          : `Der Text darf maximal ${maxLength} Zeichen lang sein`
      }
      return fieldLabel ? `${fieldLabel} ist zu groß` : 'Der Wert ist zu groß'

    case 'invalid_string':
      return 'Bitte geben Sie einen gültigen Text ein'

    case 'custom':
      // For custom validation messages, try to make them more user-friendly
      if (message) {
        // If the message is already in German, return it
        if (/[äöüÄÖÜß]/.test(message)) {
          return message
        }
        // Otherwise, provide a generic message
        return 'Bitte überprüfen Sie Ihre Eingabe'
      }
      return 'Ungültiger Wert'

    default:
      // If we have a message, try to make it more user-friendly
      if (message) {
        // Common English to German error message mappings
        const translations: Record<string, string> = {
          Required: fieldLabel ? `${fieldLabel} ist erforderlich` : 'Dieses Feld ist erforderlich',
          'Invalid input': 'Ungültige Eingabe',
          'Invalid type': 'Ungültiger Typ',
          'Number must be greater than or equal to 0': fieldLabel
            ? `${fieldLabel} muss mindestens 0 sein`
            : 'Der Wert muss mindestens 0 sein',
          'Number must be less than or equal to 100': fieldLabel
            ? `${fieldLabel} darf maximal 100 sein`
            : 'Der Wert darf maximal 100 sein',
          'Expected number, received string': 'Bitte geben Sie eine Zahl ein',
          'Expected string, received number': 'Bitte geben Sie Text ein',
        }

        // Check if we have a translation
        for (const [key, value] of Object.entries(translations)) {
          if (message.includes(key)) {
            return value
          }
        }

        // If the message is in English, provide a generic German message
        if (!/[äöüÄÖÜß]/.test(message) && /[a-zA-Z]/.test(message)) {
          return 'Bitte überprüfen Sie Ihre Eingabe'
        }

        return message
      }

      return 'Ungültiger Wert'
  }
}

/**
 * Helper to get error message for array fields (field arrays)
 */
export const getArrayFieldErrorMessage = (
  errors: Record<string, unknown>,
  fieldPath: string,
  index: number,
  subField: string
): string => {
  const pathParts = fieldPath.split('.')
  let errorObj: unknown = errors

  // Navigate to the error object
  for (const part of pathParts) {
    errorObj = (errorObj as Record<string, unknown>)?.[part]
  }

  // Get the specific error for the array item
  const error = (errorObj as Record<number, Record<string, FieldError>>)?.[index]?.[subField]

  return getFieldErrorMessage(error, subField)
}
